// Deployment trigger: 2026-06-05
import React, { useState, useEffect, useCallback } from 'react';
import { db, auth } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { Users, UserPlus, UserMinus, Mail, Loader2, AlertCircle, CheckCircle2, ShieldCheck, X, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  createUserAccount,
  updateUser,
  removeUserFromApp,
  sendResetEmail,
  ManagedUser,
  UserRole,
} from '../services/userManagementService';

interface UserManagementProps {
  // The signed-in user's role (admin or moderator). Controls available actions.
  currentRole: 'admin' | 'moderator';
}

const ROLE_OPTIONS: UserRole[] = ['user', 'moderator', 'admin'];
const PAGE_SIZE = 20;

const roleBadge: Record<UserRole, string> = {
  admin: 'text-emerald-700 bg-emerald-50',
  moderator: 'text-indigo-700 bg-indigo-50',
  user: 'text-slate-500 bg-slate-100',
};

const UserManagement: React.FC<UserManagementProps> = ({ currentRole }) => {
  const isAdmin = currentRole === 'admin';
  const myUid = auth.currentUser?.uid;

  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyUid, setBusyUid] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  // Add-user form
  const [showAdd, setShowAdd] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ email: '', username: '', password: '', role: 'user' as UserRole });

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'users'));
      const list: ManagedUser[] = snap.docs.map(d => {
        const data = d.data() as any;
        return {
          uid: d.id,
          username: data.username,
          email: data.email,
          role: (data.role as UserRole) || 'user',
        };
      });
      list.sort((a, b) => (a.username || a.email || '').localeCompare(b.username || b.email || ''));
      setUsers(list);
      setError(null);
    } catch (e: any) {
      setError('Could not load users. ' + (e?.message || ''));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const flash = (msg: string) => { setNotice(msg); setError(null); window.setTimeout(() => setNotice(null), 4000); };

  const handleAdd = async () => {
    if (!form.email || !form.password) return;
    setAdding(true);
    setError(null);
    try {
      await createUserAccount({
        email: form.email.trim(),
        username: form.username.trim() || form.email.split('@')[0],
        password: form.password,
        role: form.role,
      });
      setForm({ email: '', username: '', password: '', role: 'user' });
      setShowAdd(false);
      await refresh();
      flash('User created.');
    } catch (e: any) {
      setError(e?.message || 'Failed to create user.');
    } finally {
      setAdding(false);
    }
  };

  const handleRoleChange = async (u: ManagedUser, role: UserRole) => {
    if (role === u.role) return;
    setBusyUid(u.uid);
    setError(null);
    try {
      await updateUser({ uid: u.uid, role });
      await refresh();
    } catch (e: any) {
      setError(e?.message || 'Failed to change role.');
    } finally {
      setBusyUid(null);
    }
  };

  const handleResetEmail = async (u: ManagedUser) => {
    if (!u.email) { setError('No email on file for this user.'); return; }
    setBusyUid(u.uid);
    setError(null);
    try {
      await sendResetEmail(u.email);
      flash(`Password reset email sent to ${u.email}.`);
    } catch (e: any) {
      setError(e?.message || 'Failed to send reset email.');
    } finally {
      setBusyUid(null);
    }
  };

  const handleRemove = async (u: ManagedUser) => {
    if (!window.confirm(`Remove ${u.username || u.email} from the app? Their profile, role and access are removed. Their login still exists (they can be re-added later).`)) return;
    setBusyUid(u.uid);
    setError(null);
    try {
      await removeUserFromApp({ uid: u.uid });
      await refresh();
      flash('User removed from the app.');
    } catch (e: any) {
      setError(e?.message || 'Failed to remove user.');
    } finally {
      setBusyUid(null);
    }
  };

  // Moderators can't act on admin accounts; only admins assign the admin role.
  const canManage = (u: ManagedUser) => isAdmin || u.role !== 'admin';

  // Pagination — keep the page in range as the list shrinks/grows.
  const pageCount = Math.max(1, Math.ceil(users.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  useEffect(() => {
    if (page > pageCount - 1) setPage(pageCount - 1);
  }, [page, pageCount]);
  const pageUsers = users.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  return (
    <div className="bg-white p-8 rounded-[40px] border border-slate-200 shadow-sm">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2.5 bg-indigo-50 rounded-2xl">
          <Users className="w-5 h-5 text-indigo-600" />
        </div>
        <h3 className="font-black text-slate-800 uppercase tracking-widest text-xs">User Management</h3>
        <button
          onClick={() => setShowAdd(s => !s)}
          className="ml-auto flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all"
        >
          {showAdd ? <><X className="w-3.5 h-3.5" /> Close</> : <><UserPlus className="w-3.5 h-3.5" /> Add User</>}
        </button>
      </div>

      {error && (
        <div className="mb-5 p-3 bg-red-50 border border-red-100 rounded-xl flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
          <p className="text-[11px] text-red-600 font-bold leading-tight">{error}</p>
        </div>
      )}
      {notice && (
        <div className="mb-5 p-3 bg-emerald-50 border border-emerald-100 rounded-xl flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
          <p className="text-[11px] text-emerald-600 font-bold leading-tight">{notice}</p>
        </div>
      )}

      {showAdd && (
        <div className="mb-6 p-5 bg-indigo-50/40 rounded-2xl border border-indigo-100/60 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Email" type="email"
              className="px-4 py-3 rounded-xl border border-indigo-100 bg-white text-slate-800 text-sm font-medium outline-none focus:border-indigo-400" />
            <input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} placeholder="Username (optional)"
              className="px-4 py-3 rounded-xl border border-indigo-100 bg-white text-slate-800 text-sm font-medium outline-none focus:border-indigo-400" />
            <input value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Initial password" type="text"
              className="px-4 py-3 rounded-xl border border-indigo-100 bg-white text-slate-800 text-sm font-medium outline-none focus:border-indigo-400" />
            <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value as UserRole })}
              className="px-4 py-3 rounded-xl border border-indigo-100 bg-white text-slate-800 text-sm font-bold outline-none focus:border-indigo-400">
              <option value="user">User</option>
              <option value="moderator">Moderator</option>
              {isAdmin && <option value="admin">Admin</option>}
            </select>
          </div>
          <button onClick={handleAdd} disabled={adding || !form.email || !form.password}
            className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white py-3 rounded-xl font-black uppercase tracking-widest text-[11px] flex items-center justify-center gap-2 transition-all">
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            {adding ? 'Creating...' : 'Create User'}
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-slate-300 animate-spin" /></div>
      ) : (
        <div className="space-y-2">
          {pageUsers.map(u => {
            const manageable = canManage(u);
            const isSelf = u.uid === myUid;
            return (
              <div key={u.uid} className="p-4 bg-slate-50/60 rounded-2xl border border-slate-100 flex items-center gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-black text-slate-800 flex items-center gap-2">
                    {u.username || u.email}
                    {isSelf && <span className="text-[8px] font-black uppercase tracking-widest text-indigo-500 bg-indigo-50 px-1.5 py-0.5 rounded">You</span>}
                  </div>
                  <div className="text-[10px] text-slate-400 font-medium truncate">{u.email}</div>
                </div>

                {manageable ? (
                  <select
                    value={u.role}
                    disabled={busyUid === u.uid}
                    onChange={e => handleRoleChange(u, e.target.value as UserRole)}
                    className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-[10px] font-black uppercase tracking-widest outline-none focus:border-indigo-400 cursor-pointer disabled:opacity-50"
                  >
                    {ROLE_OPTIONS.map(r => (
                      // Only admins may assign the admin role.
                      (r !== 'admin' || isAdmin) && <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                ) : (
                  <span className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest ${roleBadge[u.role]}`}>
                    <ShieldCheck className="w-3 h-3" /> {u.role}
                  </span>
                )}

                {manageable && (
                  <button onClick={() => handleResetEmail(u)} disabled={busyUid === u.uid}
                    title="Send password reset email"
                    className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all disabled:opacity-50">
                    <Mail className="w-4 h-4" />
                  </button>
                )}

                {isAdmin && !isSelf && (
                  <button onClick={() => handleRemove(u)} disabled={busyUid === u.uid}
                    title="Remove from app"
                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all disabled:opacity-50">
                    {busyUid === u.uid ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserMinus className="w-4 h-4" />}
                  </button>
                )}
              </div>
            );
          })}
          {users.length === 0 && <p className="text-xs text-slate-400 italic text-center py-6">No users found.</p>}

          {pageCount > 1 && (
            <div className="flex items-center justify-between pt-4 mt-2 border-t border-slate-100">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, users.length)} of {users.length}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={safePage === 0}
                  className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                  Page {safePage + 1} / {pageCount}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}
                  disabled={safePage >= pageCount - 1}
                  className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserManagement;
